import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, MessageCircle, Star, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const SEOLanding = ({ keyword, breed }) => {
  const handleWhatsApp = () => {
    const message = `Hello, I found you while searching for ${keyword}. I'm interested in learning more.`;
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  const breedInfo = {
    'Golden Retriever': {
      description: 'Friendly, intelligent, and devoted family dogs. Perfect for families with children.',
      traits: ['Family-Friendly', 'Intelligent', 'Easy to Train', 'Gentle Temperament']
    },
    'Shih Tzu': {
      description: 'Affectionate, playful, and perfect for apartment living. Low maintenance companion.',
      traits: ['Apartment-Friendly', 'Low Maintenance', 'Affectionate', 'Good with Kids']
    },
    'Maltipoo': {
      description: 'Low-shedding designer breed that some people with mild allergies tolerate well. Very smart and active.',
      traits: ['Low-Shedding', 'Smart', 'Playful', 'Family-Friendly']
    },
    'Husky': {
      description: 'Loyal, energetic, and strikingly beautiful. Perfect for active families who love outdoor activities.',
      traits: ['Energetic', 'Loyal', 'Beautiful', 'Needs Exercise']
    },
    'German Shepherd': {
      description: 'Confident, courageous, and highly intelligent. Excellent working dog and loyal family protector.',
      traits: ['Intelligent', 'Protective', 'Trainable', 'Loyal']
    },
    'Labrador': {
      description: 'Outgoing, gentle, and friendly. Most popular family dog worldwide. Great with children.',
      traits: ['Family-Friendly', 'Gentle', 'Outgoing', 'Easy to Train']
    },
    'Akita': {
      description: 'Dignified, courageous, and loyal. Noble Japanese breed with impressive presence.',
      traits: ['Loyal', 'Dignified', 'Protective', 'Courageous']
    }
  };

  const currentBreed = breed ? breedInfo[breed] : null;

  return (
    <div style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
      <SEO />
      {/* Hero Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF, #DCFCE7)',
          borderBottom: '1px solid var(--neutral-border)'
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ color: 'var(--text-main)' }}>
            {keyword}
          </h1>
          <p className="text-lg mb-4" style={{ color: 'var(--text-muted)' }}>
            {breed ? `Looking for a ${breed}? ` : ''}
            Petsmart Puppies - Your Trusted Pet Store in Sector 31, Noida
          </p>

          {/* Trust Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ backgroundColor: 'var(--primary-light)' }}
          >
            <Star size={20} style={{ color: '#F59E0B', fill: '#F59E0B' }} />
            <span style={{ color: 'var(--primary-dark)', fontWeight: '600' }}>
              4.6 Google Rating
            </span>
            <span style={{ color: 'var(--text-muted)' }}>• 199+ Reviews</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/puppies/"
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)',
                display: 'inline-block'
              }}
            >
              View Available Puppies
            </Link>
            <button
              onClick={handleWhatsApp}
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: '#25D366',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <MessageCircle size={20} />
              Chat on WhatsApp
            </button>
          </div>
        </div>
      </section>

      {/* Breed Information (if specific breed) */}
      {currentBreed && (
        <section className="py-16" style={{ backgroundColor: 'white' }}>
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center" style={{ color: 'var(--text-main)' }}>
                About {breed} Puppies
              </h2>
              <p className="text-lg mb-8 text-center" style={{ color: 'var(--text-muted)' }}>
                {currentBreed.description}
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {currentBreed.traits.map((trait, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3 p-4 rounded-xl"
                    style={{ backgroundColor: 'var(--accent-light)' }}
                  >
                    <Check size={24} style={{ color: 'var(--accent-main)' }} />
                    <span className="font-semibold" style={{ color: 'var(--text-main)' }}>
                      {trait}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-16" style={{ backgroundColor: 'var(--neutral-bg)' }}>
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: 'var(--text-main)' }}>
            Why Choose Petsmart Puppies?
          </h2>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
              <div className="text-4xl mb-3">✔</div>
              <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                Healthy Puppies
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                All puppies vaccinated & health-certified
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
              <div className="text-4xl mb-3">✔</div>
              <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                Pure Breed
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Lifetime breed guarantee
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
              <div className="text-4xl mb-3">✔</div>
              <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                Affordable Prices
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Best prices in Noida
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
              <div className="text-4xl mb-3">✔</div>
              <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                Trusted
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                199+ happy customers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Available Breeds */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-main)' }}>
            Available Puppy Breeds
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {['Golden Retriever', 'Shih Tzu', 'Maltipoo', 'Poodle', 'Toy Pom', 'Maltese', 'Husky', 'Akita', 'German Shepherd', 'Labrador', 'More Breeds'].map(
              (breedName, index) => (
                <div
                  key={index}
                  className="text-center p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--primary-light)' }}
                >
                  <p className="font-semibold" style={{ color: 'var(--text-main)' }}>
                    {breedName}
                  </p>
                </div>
              )
            )}
          </div>

          <div className="text-center">
            <Link
              to="/puppies/"
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}
            >
              View All Available Puppies
            </Link>
          </div>
        </div>
      </section>

      {/* Store Location */}
      <section className="py-16" style={{ backgroundColor: 'var(--neutral-bg)' }}>
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-main)' }}>
              Visit Our Store in Sector 31, Noida
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'white' }}
              >
                <div className="flex items-start gap-3">
                  <MapPin size={24} style={{ color: 'var(--primary-main)' }} />
                  <div>
                    <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      Address
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      Shop No:- D1 Ground Floor<br />
                      Near DR Bhimrao Ambedkar Hospital<br />
                      Sector 31, Noida, UP 201303
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'white' }}
              >
                <div className="flex items-start gap-3">
                  <Phone size={24} style={{ color: 'var(--accent-main)' }} />
                  <div>
                    <h3 className="font-bold mb-2" style={{ color: 'var(--text-main)' }}>
                      Contact
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      Phone: +91 7217648679<br />
                      Open Daily until 10 PM<br />
                      Walk-ins Welcome!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <a
                href="https://maps.app.goo.gl/XswStGTLKycyKPZt7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-all hover:-translate-y-1 mr-4"
                style={{
                  backgroundColor: 'var(--primary-main)',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '9999px',
                  fontWeight: 'bold'
                }}
              >
                📍 Get Directions
              </a>
              <button
                onClick={handleWhatsApp}
                className="inline-block transition-all hover:-translate-y-1"
                style={{
                  backgroundColor: '#25D366',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '9999px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                💬 Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOLanding;
